import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { Map, Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import { Box, IconButton, Typography, Badge } from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import 'maplibre-gl/dist/maplibre-gl.css';

import LegendMap, { LEGEND_CLEARANCE } from './map_legend.js';
import { useMapSync } from './map_syncro.js';
import MapSynchronizer from './sync_maps.js';
import {
  TILE_LAYER_URL,
  BOUNDS_RECTANGLE_STYLE,
  MASK_POLYGON_STYLE,
  MAP_CONFIG
} from '../../../utils/mapUtils.js';
import FirLayers, { FirToggle } from '../../common/FirLayers.js';
import { getISOWeekAndYear } from '../../../utils/dateUtils.js';
import MapLoadingOverlay, { MapNotice } from '../../common/MapLoadingOverlay.js';
import useNearestData from '../../../hooks/useNearestData.js';

// Mismo estilo que TARGETS_CNN_STYLE en GeoServer: círculos por SEVERITY ([valor, relleno, borde]); la 3 va hueca
const LIGHTNING_SEVERITY = [[0, '#FFFF00', '#CCCC00'], [1, '#FFA500', '#CC8400'], [2, '#FF0000', '#660000'], [3, '#800080', '#4B004B']];
const lightningsLayerStyle = {
  id: 'lightnings-layer',
  type: 'circle',
  layout: { 'circle-sort-key': ['get', 'SEVERITY'] }, // más severo encima, como el zIndex del SLD
  paint: {
    'circle-radius': 3,
    'circle-stroke-width': 1,
    'circle-color': ['match', ['get', 'SEVERITY'], ...LIGHTNING_SEVERITY.flatMap(([s, fill]) => [s, fill]), '#FFFF00'],
    'circle-stroke-color': ['match', ['get', 'SEVERITY'], ...LIGHTNING_SEVERITY.flatMap(([s, , stroke]) => [s, stroke]), '#CCCC00'],
    'circle-opacity': ['case', ['==', ['get', 'SEVERITY'], 3], 0, 0.8], // 0,8 = opacidad que tenía la capa WMS
    'circle-stroke-opacity': 0.8,
  },
};

// Capa de rayos → capa del backend que busca el día con ground truth más cercano (la de 00625 no está en el selector)
const NEAREST_LIGHTNINGS = {
  'TARGETS_CNN:lightnings_EU_025': 'lightnings_eu_025',
  'TARGETS_CNN:lightnings_EU_01': 'lightnings_eu_01',
};

const MapRightContent = ({
  mapRightLayer,
  cqlFecha,
  metricsNames,
  issueTime,
  onGoToIssue,
  showLegend,

  rectBounds,   // Recibido del padre
}) => {
  // El <Map id="mapRight"> que envuelve a este componente. useMap().mapRight es undefined: los mapas por id solo
  // existen con <MapProvider>, que aquí no hay (así nunca se registraba el clic)
  const { current: map } = useMap();
  const [loading, setLoading] = useState(false);
  const [lightnings, setLightnings] = useState(null);
  // Hora ya pintada mientras llega el día entero
  const [slotTime, setSlotTime] = useState(null);
  const [clickedInfo, setClickedInfo] = useState(null);
  // 'error' | 'empty': antes los dos dejaban el mapa en blanco sin aviso; Retry (retry + 1) repite la petición
  const [fail, setFail] = useState(null);
  const [retry, setRetry] = useState(0);

  // Rayos (ground truth, horarios): el día entero en una petición y el slider solo filtra la hora en el mapa, como
  // visibility en map_left. Con teselas WMS cada paso del slider volvía a pedir ~20 teselas (3-6 s en frío) con el mapa
  // en blanco. `slot` pide solo la hora del slider para pintarla mientras llega el día (2-7 MB)
  const day = cqlFecha?.split(' ')[0];
  const lightningUrls = useMemo(() => {
    if (!mapRightLayer || !day) return null;
    // Particiones por (YEAR, WEEK_NUMBER): con los dos Postgres abre 1 partición en vez de 5 (una por año), ~1 s menos
    // en frío. Aquí YEAR es el año natural, no el ISO (el 29-12-2025 está como YEAR 2025, semana 1).
    // WEEK_NUMBER es la semana ISO: el prop weekNumber (date-fns getWeek) empieza en domingo y los domingos pedía la
    // semana siguiente, sin rayos (domingo 2026-10-04: semana 40 → 6497 puntos, 41 → 0)
    const { week } = getISOWeekAndYear(`${day}T00:00:00`);
    const wfs = (cql) => `service=WFS&version=1.0.0&request=GetFeature&typeName=${mapRightLayer}&outputFormat=application/json&CQL_FILTER=${encodeURIComponent(`${cql} AND WEEK_NUMBER=${week} AND YEAR=${day.slice(0, 4)}`)}`;
    const query = wfs(`VALIDDATE >= '${day} 00:00:00' AND VALIDDATE <= '${day} 23:59:59'`);
    return {
      // Endpoint propio (Redis; 30 días para días cerrados); backup = proxy WFS genérico, por si el backend aún no lo tiene
      primary: `${process.env.REACT_APP_API_URL}/proxy/wfs/lightnings_historical?${query}`,
      backup: `${process.env.REACT_APP_API_URL}/proxy/wfs?${query}`,
      slot: (validTime) => `${process.env.REACT_APP_API_URL}/proxy/wfs/lightnings_historical?${wfs(`VALIDDATE='${validTime}'`)}`,
    };
  }, [mapRightLayer, day]);

  // Otra capa (resolución): fuera los puntos de la anterior mientras llegan los nuevos
  useEffect(() => setLightnings(null), [mapRightLayer]);
  // El popup es de una hora concreta: al cambiar de hora o de capa se cierra
  useEffect(() => setClickedInfo(null), [cqlFecha, mapRightLayer]);

  // cqlFecha solo decide la hora que se pide primero: moverla no debe relanzar la petición
  useEffect(() => {
    if (!lightningUrls) return;
    const controller = new AbortController();
    const { signal } = controller;
    let fullLoaded = false;
    setLoading(true);
    setSlotTime(null);
    setFail(null);
    // La hora del slider va en paralelo con el día entero y, si llega antes, se pinta ya. Si falla, el día la trae igual
    axios.get(lightningUrls.slot(cqlFecha), { signal, timeout: 30000 })
      .then(({ data }) => {
        if (!fullLoaded && !signal.aborted && data?.features?.length) {
          setLightnings(data);
          setSlotTime(cqlFecha);
        }
      })
      .catch(() => {});
    (async () => {
      for (const url of [lightningUrls.primary, lightningUrls.backup]) {
        try {
          // timeout: sin él una petición colgada dejaba el aviso de carga para siempre
          const { data } = await axios.get(url, { signal, timeout: 30000 });
          // GeoServer devuelve sus errores como XML con estado 200: MapLibre tomaba ese texto por una URL
          if (!Array.isArray(data?.features)) throw new Error('respuesta sin GeoJSON');
          fullLoaded = true;
          setLightnings(data);
          if (!data.features.length) setFail('empty');
          break;
        } catch (err) {
          if (axios.isCancel(err)) return;
          console.warn('[MapRight] Fallo cargando rayos, se prueba la siguiente fuente:', err.message);
        }
      }
      if (!fullLoaded) setFail('error');
      setLoading(false);
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightningUrls, retry]);

  // Día sin rayos: como el aviso del mapa izquierdo, se busca el día con ground truth más cercano y el aviso dice cuál es
  // y ofrece ir a él. Se va con el issue de ahora (D-0 de ese día): si el mapa izquierdo no lo tiene, dirá el suyo
  const missingTruth = !loading && fail === 'empty' && !!NEAREST_LIGHTNINGS[mapRightLayer];
  const { nearest, searching: searchingNearest } = useNearestData(NEAREST_LIGHTNINGS[mapRightLayer], day, '', missingTruth && !!onGoToIssue, retry);
  const issueHour = issueTime?.match(/[T\s](\d{2}):/)?.[1] || '01';

  // Info del punto con los datos ya cargados (antes, un GetFeatureInfo a GeoServer por clic); ±3 px para acertar el círculo
  const onMapClick = useCallback((e) => {
    const nativeMap = map?.getMap();
    if (!nativeMap?.getLayer('lightnings-layer')) return setClickedInfo(null);
    const { x, y } = e.point;
    const [feature] = nativeMap.queryRenderedFeatures([[x - 3, y - 3], [x + 3, y + 3]], { layers: ['lightnings-layer'] });
    setClickedInfo(feature ? { lngLat: e.lngLat, feature } : null);
  }, [map]);

  useEffect(() => {
    if (!map) return;
    map.on('click', onMapClick);
    return () => map.off('click', onMapClick);
  }, [map, onMapClick]);

  const maskGeoJson = useMemo(() => {
    if (!rectBounds) return null;
    const [[latN, lonW], [latS, lonE]] = rectBounds;
    const innerRing = [[lonW, latN], [lonE, latN], [lonE, latS], [lonW, latS], [lonW, latN]];
    const outerRing = [[-180, 90], [180, 90], [180, -90], [-180, -90], [-180, 90]];
    return { type: 'Feature', geometry: { type: 'Polygon', coordinates: [outerRing, innerRing] } };
  }, [rectBounds]);

  return (
    <>
      <MapSynchronizer />
      {/* Tarjeta hasta que se ve la hora del slider; con ella pintada, píldora hasta que llega el resto del día */}
      <MapLoadingOverlay open={loading} compact={slotTime === cqlFecha} layer="Ground Truth" time={cqlFecha} />
      {/* Con la leyenda abierta el aviso sube sobre ella: abajo a la derecha quedaba debajo y tapaba su botón */}
      {missingTruth && nearest?.date && onGoToIssue
        ? <MapNotice kind="available" empty={`No ground truth for ${day}`} detail={`Closest available data: ${nearest.date}`} onRetry={() => onGoToIssue(nearest.date, issueHour)} action="Go to date" bottom={showLegend ? LEGEND_CLEARANCE : undefined} />
        : !searchingNearest && <MapNotice kind={loading ? null : fail} empty="No ground truth for this day" onRetry={() => setRetry((n) => n + 1)} bottom={showLegend ? LEGEND_CLEARANCE : undefined} />}
      {lightnings && (
        <Source id="lightnings-source" type="geojson" data={lightnings}>
          <Layer {...lightningsLayerStyle} filter={['==', ['get', 'VALIDDATE'], `${cqlFecha.replace(' ', 'T')}Z`]} />
        </Source>
      )}
      {maskGeoJson && (
        <Source type="geojson" data={maskGeoJson}>
          <Layer id="mask-layer" type="fill" paint={{ 'fill-color': MASK_POLYGON_STYLE.fillColor, 'fill-opacity': MASK_POLYGON_STYLE.fillOpacity }} />
        </Source>
      )}
      {rectBounds && (
        <Source type="geojson" data={{
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [
              [rectBounds[0][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[0][0]],
              [rectBounds[1][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[1][0]],
              [rectBounds[0][1], rectBounds[0][0]]
            ]
          }
        }}>
          <Layer id="bounds-layer" type="line" paint={{ 'line-color': BOUNDS_RECTANGLE_STYLE.color, 'line-width': BOUNDS_RECTANGLE_STYLE.weight, 'line-dasharray': [3, 5] }} />
        </Source>
      )}
      {clickedInfo && (
        <Popup longitude={clickedInfo.lngLat.lng} latitude={clickedInfo.lngLat.lat} onClose={() => setClickedInfo(null)} anchor="bottom">
          <Box sx={{ p: 1 }}>
            <Typography variant="subtitle2">Info:</Typography>
            <pre style={{ fontSize: '10px' }}>{JSON.stringify(clickedInfo.feature.properties, null, 2)}</pre>
          </Box>
        </Popup>
      )}
    </>
  );
};

const MapRight = ({
  cqlSevere,
  cqlFecha,
  mapRightLayer,
  weekNumber,
  issueTime,
  selectedModel,
  isInteractive = true,
  metricsNames,
  onGoToIssue
}) => {
  const [showLegend, setShowLegend] = useState(true);
  const [showFirs, setShowFirs] = useState(true);
  const { mapState } = useMapSync();
  const mapRef = useRef(null);

  // Mover cálculos de límites aquí para que handleReset pueda verlos
  const rectBounds = useMemo(() => {
    if (!metricsNames) return null;
    return metricsNames.includes('01')
      ? [[33, -16], [64, 45]]
      : [[33, -27], [73, 45]];
  }, [metricsNames]);

  const mapBounds = useMemo(() => {
    if (!rectBounds) return null;
    return [rectBounds[0][1], rectBounds[0][0], rectBounds[1][1], rectBounds[1][0]];
  }, [rectBounds]);

  const handleToggleLegend = () => setShowLegend(!showLegend);

  const handleReset = () => {
    if (mapRef.current && mapBounds) {
      mapRef.current.fitBounds(mapBounds, {
        padding: 60,
        duration: 1000
      });
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Map
        id="mapRight"
        onLoad={(e) => { mapRef.current = e.target; }}
        initialViewState={{
          latitude: mapState.center[0],
          longitude: mapState.center[1],
          zoom: mapState.zoom
        }}
        mapStyle="/map-styles/kairos/forecast-metrics.json"
        attributionControl={false}
        style={{ width: '100%', height: '100%' }}
        dragPan={isInteractive}
        scrollZoom={isInteractive}
        boxZoom={isInteractive}
        doubleClickZoom={isInteractive}
        touchZoomRotate={isInteractive}
        minZoom={MAP_CONFIG.minZoom}
        maxZoom={MAP_CONFIG.maxZoom}
        fadeDuration={0}
        collectResourceTiming={false}
      >
        <FirLayers visible={showFirs} />
        <MapRightContent
          mapRightLayer={mapRightLayer}
          cqlFecha={cqlFecha}
          metricsNames={metricsNames}
          issueTime={issueTime}
          onGoToIssue={onGoToIssue}
          showLegend={showLegend}
          isInteractive={isInteractive}
          rectBounds={rectBounds}
        />
      </Map>

      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Badge
          badgeContent={
            <Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.55rem' }}>
              RESET ZOOM
            </Typography>
          }
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          sx={{
            '& .MuiBadge-badge': {
              padding: '0 8px',
              height: '18px',
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              top: '0px',
              left: '-2px',
              transform: 'translateX(-100%)',
              borderRadius: '10px',
            },
          }}
        >
          <IconButton
            title='Reset view'
            onClick={handleReset}
            sx={{
              bgcolor: '#ffffff',
              color: '#38a477',
              '&:hover': { bgcolor: '#f5f5f5' },
              width: { xs: 28, md: 36 },
              height: { xs: 28, md: 36 },
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              borderRadius: 10,
              opacity: 0.9,
            }}
          >
            <HomeIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
          </IconButton>
        </Badge>

        <FirToggle visible={showFirs} onToggle={() => setShowFirs(!showFirs)} />
      </Box>

      <IconButton
        title='Information'
        onClick={handleToggleLegend}
        sx={{
          position: 'absolute',
          bottom: 10,
          right: 10,
          zIndex: 10,
          bgcolor: '#ffffff',
          color: '#38a477df',
          '&:hover': { bgcolor: '#f5f5f5' },
          width: { xs: 28, md: 36 },
          height: { xs: 28, md: 36 },
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
          border: '1px solid rgba(0, 0, 0, 0.05)',
          borderRadius: 10,
          opacity: 0.9,
        }}
      >
        <InfoIcon sx={{ fontSize: { xs: 18, md: 22 } }} />
      </IconButton>

      {showLegend && <LegendMap variant="right" selectedModel={selectedModel} />}
    </Box>
  );
};

export default MapRight;